import CircularLoadingDots from "components/utils/CircularLoadingDots";
import { ChangeEvent, useState } from "react";
import { MdOutlinePublish } from "react-icons/md";
import { acceptToMIMEFormats } from "utils/utils";

interface InputFileProps {
  title: string;
  setFile: React.Dispatch<React.SetStateAction<string | null>>;
  setFilename: React.Dispatch<React.SetStateAction<string>>;
  setFilesize: React.Dispatch<React.SetStateAction<number>>;
  onLoadEnd?: () => void;
  accept?: string;
  maxMb?: number;
  keyName: string;
}

const InputFile: React.FC<InputFileProps> = ({
  title,
  setFile,
  setFilename,
  setFilesize,
  onLoadEnd,
  accept = ".pdf",
  maxMb = 10,
  keyName,
}) => {
  // Parameters definition
  const formatsAllowed = acceptToMIMEFormats(accept);
  const maxFileSize = maxMb * 1024 * 1024; // 10 MB

  // States definition
  const [isDragging, setIsDragging] = useState(false);
  const [isLocked, setIsLocked] = useState(false);

  /**
   * Function to handle PDF plan uploading
   * @param event
   */
  const handleFileUpload = (event: ChangeEvent<HTMLInputElement>) => {
    // Get the file
    const file = event.target.files?.[0];

    // Process the file
    processFileUpload(file);
  };

  /**
   * Function to process file uploading
   * @param event
   */
  const processFileUpload = (file: File | undefined) => {
    // Check if there is an inputted file
    if (!file) return;

    // Lock the input component
    setIsLocked(true);

    // Check if the of the uploaded file is the defined in the
    // formats list allowed for this placeholder
    if (!formatsAllowed.includes(file.type)) {
      alert(`Format "${file.type}" is not allowed.`);
      setIsLocked(false);
      return;
    }

    // Check that the size is the allowed one
    if (file.size > maxFileSize) {
      const sizeInMb = (file.size / 1024 / 1024).toFixed(2);
      alert(`File size (${sizeInMb} MB) is larger than allowed.`);
      setIsLocked(false);
      return;
    }

    // Perform the loading
    const reader = new FileReader();
    reader.onloadend = () => {
      setFile(reader.result as string);
      setFilename(file.name);
      setFilesize(file.size);

      // Execute a custom optional function if defined
      if (onLoadEnd) onLoadEnd();
    };
    reader.readAsDataURL(file);
  };

  /**
   * Function used on drop the area
   * @param event
   */
  const handleDrop = (event: React.DragEvent) => {
    // Prevent opening the file on another tab
    event.preventDefault();

    // Set dragging state to false
    setIsDragging(false);

    // Get the list of files dropped
    const droppedFiles = event.dataTransfer.files;

    // Check if the drop contain files
    if (droppedFiles.length > 0) {
      // Transform into an array
      const newFiles = Array.from(droppedFiles);

      // And use just the first file
      processFileUpload(newFiles[0]);
    }
  };

  /**
   * Function used to control drag and drop behaviour when leaving
   * @param event
   */
  const handleDragLeave = (event: React.DragEvent) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragging(false);
  };

  /**
   * Function used to control drag and drop behaviour when leaving
   * @param event
   */
  const handleDragOver = (event: React.DragEvent) => {
    event.preventDefault();
    event.stopPropagation();
  };

  /**
   * Function used to control drag and drop behaviour when entering
   * @param event
   */
  const handleDragEnter = (event: React.DragEvent) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragging(true);
  };

  /**
   * Function used to allow opening the upload file window when pressing
   * enter after being selected by tab button.
   * @param event
   */
  const pressEnter = (event: React.KeyboardEvent<HTMLButtonElement>) => {
    if (event.key === "Enter") {
      const inputFile =
        document && document.getElementById(`file-upload-${keyName}`);
      if (inputFile) {
        inputFile.click();
      }
    }
  };

  return (
    <button
      className={`self-stretch rounded-xl border ${
        isDragging
          ? "border-secondary-500 border-solid"
          : "border-neutral-300 border-dashed"
      } hover:bg-neutral-25 ease-in-out duration-150`}
      onKeyDown={pressEnter}
    >
      {isLocked ? (
        // Waiting animation
        <div className="w-full h-full px-2 py-4 justify-center items-center gap-2 inline-flex relative">
          <CircularLoadingDots nDots={10} className="!size-10" />
        </div>
      ) : (
        <div
          className={`w-full h-full px-2 py-4 justify-center items-center gap-2 inline-flex relative`}
          onDrop={handleDrop}
          onDragEnter={handleDragEnter}
          onDragLeave={handleDragLeave}
          onDragOver={handleDragOver}
        >
          <MdOutlinePublish className="w-10 h-10 fill-neutral-500" />
          <input
            type="file"
            accept={accept}
            onChange={handleFileUpload}
            className="hidden"
            id={`file-upload-${keyName}`}
          />
          {/* Label used to hide the on-hover text popup, also helps fix some alignment issues */}
          <label
            id={`file-upload-label-${keyName}`}
            htmlFor={`file-upload-${keyName}`}
            className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
          />
          <div className="flex-col justify-center items-start gap-0.5 inline-flex">
            <div className="text-neutral-600 text-xs font-normal font-sans leading-[14px] select-none">
              {title}
            </div>
            <div className="justify-start items-center gap-2 inline-flex">
              <div className="text-neutral-500 text-[10px] font-sans leading-3 tracking-tight select-none">
                <span className="font-normal">(File type: </span>
                <span className="font-semibold">{accept}</span>
                <span className="font-normal">, Max. file size: </span>
                <span className="font-semibold">{maxMb} MB</span>
                <span className="font-normal">)</span>
              </div>
            </div>
          </div>
        </div>
      )}
    </button>
  );
};

export default InputFile;
